/**
 * Centrale pricing-config voor Powersure Worldwide (frontend mirror).
 *
 * 🟢 ACTIEVE VERKOOPSTRATEGIE (Feb 2026):
 *   Powersure Energy Control =
 *     - Eerste 3 maanden:  € 9,95 / maand   (introductietarief)
 *     - Daarna doorlopend: € 14,95 / maand  (reguliere prijs)
 *   Wordt automatisch afgehandeld via een Stripe Subscription Schedule
 *   in `services/monitoring_promo_schedule.py`.
 *
 * ⚠️ GRANDFATHERING:
 *   - Bestaande grandfathered abonnees blijven op hun eigen Stripe Price
 *     (€ 9,95 / m). Hun subscription wordt NIET aangeraakt.
 *   - BROAD_SCAN_PRICE_EUR (75) — Fase 2 reserve, niet aan checkout.
 *
 * Mirror van services/pricing_config.py — houd handmatig in sync.
 */

// --- Active promo pricing ---
export const EC_INTRO_PRICE_EUR = 9.95;
export const EC_REGULAR_PRICE_EUR = 14.95;
export const EC_INTRO_MONTHS = 3;
export const EC_INTRO_LABEL = "Introductietarief";

// --- Side prices ---
export const BROAD_SCAN_PRICE_EUR = 75.0;          // Fase 2 reserve
export const LEGACY_MONITORING_PRICE_EUR = 9.95;   // grandfathered abonnees

// --- Legacy admin-only diagnostics (NIET klant-facing) ---
export const LEGACY_EC_V2_PRICE_EUR = 19.95;
// Backwards-compat shim — verwijst nu naar de reguliere promo-prijs.
export const ENERGY_CONTROL_MONTHLY_PRICE_EUR = EC_REGULAR_PRICE_EUR;

/**
 * Formatteer een bedrag in NL-locale: 9.95 -> "€ 9,95".
 */
export function formatEur(amount, { withCurrencySymbol = true } = {}) {
  const formatted = Number(amount).toFixed(2).replace(".", ",");
  return withCurrencySymbol ? `€ ${formatted}` : formatted;
}

/**
 * Korte intro-label voor knoppen/CTAs.
 * @example "€ 9,95/m"
 */
export function energyControlIntroShortLabel({ suffix = "/m" } = {}) {
  return `${formatEur(EC_INTRO_PRICE_EUR)}${suffix}`;
}

/**
 * Korte reguliere-prijs label.
 * @example "€ 14,95/m"
 */
export function energyControlRegularShortLabel({ suffix = "/m" } = {}) {
  return `${formatEur(EC_REGULAR_PRICE_EUR)}${suffix}`;
}

/**
 * Volledige promo-zin in klant-facing copy.
 * @example "€ 9,95 per maand voor de eerste 3 maanden, daarna € 14,95 per maand · maandelijks opzegbaar"
 */
export function energyControlPromoFullLabel() {
  return (
    `${formatEur(EC_INTRO_PRICE_EUR)} per maand voor de eerste ${EC_INTRO_MONTHS} maanden, ` +
    `daarna ${formatEur(EC_REGULAR_PRICE_EUR)} per maand · maandelijks opzegbaar`
  );
}

/**
 * Korte promo-zin voor pricing-pills / CTAs.
 * @example "€ 9,95/m · eerste 3 mnd · daarna € 14,95/m"
 */
export function energyControlPromoShortLabel() {
  return (
    `${formatEur(EC_INTRO_PRICE_EUR)}/m · eerste ${EC_INTRO_MONTHS} mnd · ` +
    `daarna ${formatEur(EC_REGULAR_PRICE_EUR)}/m`
  );
}

// --- Backwards compatibility aliases ---
// Older components still import these names. Keep them pointing at the
// new promo helpers so we don't break the build during the migration.
export function energyControlIntroFullLabel() {
  return energyControlPromoFullLabel();
}

export function energyControlMonthlyLabel() {
  return energyControlPromoFullLabel();
}

/**
 * Stripe-werkelijke prijs (admin/diagnostic-only — niet tonen op
 * klant-facing pages).
 */
export function energyControlStripeLabel({ suffix = "/m" } = {}) {
  return `${formatEur(LEGACY_EC_V2_PRICE_EUR)}${suffix}`;
}

/**
 * Klantgerichte label voor brede scan. Voorbeeld: "€ 75".
 */
export function broadScanLabel({ suffix = "" } = {}) {
  return `${formatEur(BROAD_SCAN_PRICE_EUR)}${suffix}`;
}

/**
 * Grandfathered abonnees-label. GEBRUIK ALLEEN voor bestaande abonnees.
 */
export function legacyMonitoringLabel({ suffix = "/m" } = {}) {
  return `${formatEur(LEGACY_MONITORING_PRICE_EUR)}${suffix}`;
}
